import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router";
import { useNavigate } from "react-router-dom";
import { isLoggedIn } from "../api/auth";
import { workbooksApi } from "../api/workbooks";
import { catalogueApi } from "../api/catalogue";
import WorkbookLayout from "../components/layout/WorkbookLayout";
import { currentPath, loginUrlFor } from "../lib/nextUrl";
import LoadErrorPanel from "../components/LoadErrorPanel";
import {
  settled,
  isAnswered,
  isFailed,
  LOADING_STATE,
} from "../lib/fetchState";

const WorkbookPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const loggedIn = isLoggedIn();
  const id = slug || "csharp-mindset";

  const [userAccess, setUserAccess] = useState(false);
  const [checking, setChecking] = useState(true);
  // How the workbook read went, apart from what it returned. userAccess starts
  // false, so a failed read used to be indistinguishable from "you do not have
  // access": an owner whose request dropped got the locked overlay, or the
  // "workbook not found" page, over content they had paid for.
  const [accessState, setAccessState] = useState(LOADING_STATE);
  const [workbook, setWorkbook] = useState(null);
  const [sections, setSections] = useState([]);
  // The bundle this workbook belongs to, for the locked-section unlock offer.
  // Loaded once and matched by id, so the overlay always sells the bundle the
  // reader is actually in rather than a track guessed from a string.
  const [bundles, setBundles] = useState([]);
  // Bumped by Retry to re-run the load effect.
  const [reloadKey, setReloadKey] = useState(0);

  // The bundle copy behind the locked-section unlock offer. Its outcome is
  // tracked too: without that, a dropped /api/bundles request left the overlay
  // saying "Not on sale yet", which is an authoritative-sounding answer to a
  // question the page never got an answer to.
  const [bundlesState, setBundlesState] = useState(LOADING_STATE);
  const loadBundles = useCallback(() => {
    return settled(catalogueApi.getBundles()).then((result) => {
      setBundlesState(result);
      if (isAnswered(result)) setBundles(result.data || []);
    });
  }, []);

  useEffect(() => {
    loadBundles();
  }, [loadBundles]);

  useEffect(() => {
    if (!loggedIn) {
      // replace, because this is a redirect caused by state: pushing meant back
      // returned here, which redirected again, and the reader could never get
      // out. ?next= brings them to this workbook, and this section, after they
      // sign in.
      //
      // The URL is read from window at redirect time rather than taken as a
      // dependency: the section lives in ?s=, and this effect must not refetch
      // the workbook every time the reader turns a page.
      navigate(loginUrlFor(currentPath()), { replace: true });
      return;
    }

    const isMounted = { current: true };
    setAccessState(LOADING_STATE);
    setChecking(true);
    settled(workbooksApi.getOne(id)).then((result) => {
      if (!isMounted.current) return;
      setAccessState(result);
      // Only an answer sets access. A failure leaves it alone, so nothing
      // downstream reads a dropped request as a locked section.
      if (isAnswered(result) && result.data) {
        setWorkbook(result.data.workbook);
        setSections(result.data.sections);
        setUserAccess(result.data.hasAccess);
      }
      setChecking(false);
    });
    return () => { isMounted.current = false; };
  }, [loggedIn, id, reloadKey, navigate]);

  // Access, re-read on demand rather than only at mount.
  //
  // Unlocking a section used to reload the document so the reader came back
  // knowing it was open. It settles in place now, so this is what tells the
  // reader the section is unlocked: the same shape of refresh the bundle page
  // uses, asking the server rather than assuming the payment succeeded.
  // Sections are re-read too, because a locked section arrives with its blocks
  // stripped and only the server can fill them in.
  const refreshAccess = useCallback(() => {
    return settled(workbooksApi.getOne(id)).then((result) => {
      // Deliberately does not set accessState on failure: this runs on every
      // tab focus and straight after a purchase, and swapping a working reader
      // for an error panel because one background read dropped would be worse
      // than showing slightly stale access. Stale-but-true beats a false "no".
      if (!isAnswered(result)) {
        console.error("Access refresh failed:", result.error);
        return;
      }
      if (!result.data) return;
      setAccessState(result);
      setWorkbook(result.data.workbook);
      setSections(result.data.sections);
      setUserAccess(result.data.hasAccess);
    });
  }, [id]);

  // Coming back to the tab is the webhook-only case: on mobile UPI the buyer
  // leaves for their bank app, the webhook confirms and grants while they are
  // away, and the handler that would have told us never runs. Ask the server
  // what changed. Same rule as the bundle page.
  useEffect(() => {
    if (!loggedIn) return;
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshAccess();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [loggedIn, refreshAccess]);

  if (!loggedIn) return null;

  if (checking)
    return (
      <div
        style={{
          padding: "3rem",
          textAlign: "center",
          color: "#71717a",
          fontFamily: "sans-serif",
        }}
      >
        Loading...
      </div>
    );

  // The read did not come back. The reader cannot know whether this person has
  // access, so it shows neither the locked overlay nor a not-found page.
  if (isFailed(accessState))
    return (
      <LoadErrorPanel
        title="We could not load this workbook"
        message="Something went wrong reaching the server, so we cannot confirm what you have access to. Nothing has changed in your account."
        onRetry={() => setReloadKey((k) => k + 1)}
        retrying={checking}
      />
    );

  if (!workbook)
    return (
      <div
        style={{
          padding: "3rem 1.5rem",
          textAlign: "center",
          fontFamily: "sans-serif",
        }}
      >
        <h2 style={{ marginBottom: ".5rem" }}>Workbook not found</h2>
        <p style={{ color: "#71717a" }}>No workbook with id "{id}" exists.</p>
        <a
          href="/workbooks"
          style={{
            color: "#1e1b4b",
            marginTop: "1rem",
            display: "inline-block",
          }}
        >
          ← All workbooks
        </a>
      </div>
    );

  return (
    <div style={{ background: "#f5f2ec", minHeight: "100vh", paddingTop: 30 }}>
      <WorkbookLayout
        workbook={{ ...workbook, sections }}
        userAccess={userAccess}
        bundle={bundles.find((b) => b.id === workbook?.bundleId) || null}
        bundleUnavailable={isFailed(bundlesState)}
        onRetryBundle={loadBundles}
        onRefreshAccess={refreshAccess}
      />
    </div>
  );
};

export default WorkbookPage;
